import { useEffect, useState } from 'react';
import { AritoFormV2, HeaderTabsV2, DialogV2, AritoIcon } from '@/components/custom/erp';
import { AdditionalInfoTab, BasicInfoTab, GeneralTab, HDDTTab, OtherTab } from './tabs';
import { FormSchema, type FormValues, initialFormValues } from '../../schema';
import type { NhaCungCap, NhaCungCapInput } from '@/types/schemas';
import { getFormActionButtons, getFormTitle } from '../../utils';
import type { FormMode } from '@/types/form';
import { useFormState } from '../../hooks';

interface FormDialogProps {
  formMode: FormMode;
  mode?: FormMode;
  open: boolean;
  initialData?: NhaCungCap;
  onSubmit?: (data: NhaCungCapInput) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  zIndex?: number;
}

const FormDialog = ({
  open,
  onClose,
  onSubmit,
  formMode,
  mode,
  initialData,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  zIndex
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const { state, actions } = useFormState(initialData);

  const resolvedMode = mode || formMode;

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
    }
  }, [open]);

  const handleSubmit = (data: FormValues) => {
    onSubmit?.({
      ...data,
      is_customer: false,
      is_vendor: true,
      birth_date: data.birth_date || null,
      sales_rep: state.nhanVien?.uuid,
      account: state.taiKhoanNgamDinh?.uuid,
      payment_term: state.thanhToan?.uuid,
      payment_method: state.phuongThucThanhToan?.uuid,
      customer_group1: state.nhom1?.uuid,
      customer_group2: state.nhom2?.uuid,
      customer_group3: state.nhom3?.uuid,
      region: state.khuVuc?.uuid
    } as NhaCungCapInput);
    setIsFormDirty(false);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const title = getFormTitle(resolvedMode, activeTab, initialData?.customer_code);
  const actionButtons = getFormActionButtons(resolvedMode, activeTab, {
    onAdd: onAdd,
    onEdit: onEdit,
    onDelete: onDelete,
    onCopy: onCopy,
    handleClose: onClose
  });

  const formContent = (
    <AritoFormV2
      mode={resolvedMode}
      title={zIndex ? undefined : title}
      actionButtons={actionButtons}
      subTitle={zIndex ? undefined : 'Danh mục nhà cung cấp'}
      schema={FormSchema}
      onSubmit={handleSubmit}
      onClose={onClose}
      initialData={initialData || initialFormValues}
      className='w-full'
      headerFields={
        <div onChange={() => setIsFormDirty(true)}>
          <HeaderTabsV2
            tabs={[
              {
                id: 'info',
                label: 'Thông tin cơ bản',
                component: <BasicInfoTab formMode={resolvedMode} />
              }
            ]}
            onTabChange={handleTabChange}
            defaultTabIndex={activeTab === 'info' ? 0 : activeTab === 'price' ? 1 : activeTab === 'review' ? 2 : 0}
          />
        </div>
      }
      tabs={
        <HeaderTabsV2
          tabs={[
            {
              id: 'basic-info',
              label: 'Thông tin cơ bản',
              component: <GeneralTab formMode={resolvedMode} formState={{ state, actions }} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={resolvedMode} />
            },
            {
              id: 'additional-info',
              label: 'Thông tin thêm',
              component: <AdditionalInfoTab formMode={resolvedMode} />
            },
            {
              id: 'hddt',
              label: 'HDDT',
              component: <HDDTTab formMode={resolvedMode} />
            }
          ]}
        />
      }
    />
  );

  if (zIndex !== undefined) {
    return (
      <DialogV2
        open={open}
        onClose={onClose}
        title={
          resolvedMode === 'add'
            ? 'Thêm mới nhà cung cấp'
            : resolvedMode === 'edit'
              ? 'Sửa nhà cung cấp'
              : 'Xem chi tiết'
        }
        maxWidth='xl'
        disableBackdropClose={true}
        disableEscapeKeyDown={true}
        titleIcon={<AritoIcon icon={281} />}
        zIndex={zIndex}
        paperClassName='w-[min(980px,calc(100vw-16px))]'
      >
        <div className='h-[min(650px,calc(100vh-150px))] overflow-y-auto p-4'>{formContent}</div>
      </DialogV2>
    );
  }

  return formContent;
};

export default FormDialog;
